/** Reload only on a collection page, outside forms, dialogs and API requests. */
export function createApplicationUpdate({
  window: win = typeof window === 'undefined' ? null : window,
  document: doc = typeof document === 'undefined' ? null : document,
  timers = globalThis,
  now = Date.now,
} = {}) {
  const requests = new Set();
  let requestId = 0;
  let safeRoute = () => false;
  let pending = null;
  let reloadTimer = null;
  let reloading = false;
  const visible = () => (!doc || doc.visibilityState !== 'hidden') && win?.navigator?.onLine !== false;
  function safe() {
    return visible() && safeRoute() && !requests.size
      && !doc?.querySelector('[role="dialog"], [aria-modal="true"], .employee-table-editable-cell--editing, .p-cell-editing')
      && !doc?.activeElement?.matches('input, textarea, select, [contenteditable="true"]');
  }
  function attemptReload() {
    if (!pending || reloading || !safe()) return;
    const { key, beforeReload, target, once, sourceHref } = pending;
    // A timestamp, rather than a navigation-reset flag, prevents reload loops.
    try {
      const previous = Number(win.sessionStorage.getItem(key));
      if (previous && (once || now() - previous < 120_000)) return;
      win.sessionStorage.setItem(key, String(now()));
    } catch { /* The in-memory guard still prevents duplicate reloads. */ }
    reloading = true;
    if (reloadTimer !== null) timers.clearInterval(reloadTimer);
    reloadTimer = null;
    Promise.resolve().then(() => beforeReload?.()).catch(() => {}).then(() => {
      // Navigation and interaction can change while a chunk is being repaired.
      if (!safe()) {
        reloading = false;
        try { win.sessionStorage.removeItem(key); } catch { /* optional storage */ }
        reloadTimer = timers.setInterval(attemptReload, 1_000);
        return;
      }
      const followTarget = target && sourceHref === win.location.href;
      try {
        if (!followTarget || target === `${win.location.pathname}${win.location.search}${win.location.hash}`) {
          win.sessionStorage.setItem('hr:reload-scroll', JSON.stringify({
            href: win.location.href, x: win.scrollX, y: win.scrollY, at: now(),
          }));
        }
      } catch { /* Scroll restoration is optional. */ }
      if (followTarget && target !== `${win.location.pathname}${win.location.search}${win.location.hash}`) win.location.assign(target);
      else win.location.reload();
    });
  }
  function requestReload(options = {}) {
    if (!win) return false;
    pending = { key: 'hr:application-reload', sourceHref: win.location.href, ...pending, ...options };
    if (reloadTimer === null && !reloading) reloadTimer = timers.setInterval(attemptReload, 1_000);
    return true;
  }
  function trackRequest(config) {
    const id = ++requestId;
    requests.add(id);
    config.__hrUpdateRequest = id;
  }
  function finishRequest(config) { requests.delete(config?.__hrUpdateRequest); }
  function stop() {
    if (reloadTimer !== null) timers.clearInterval(reloadTimer);
    reloadTimer = null;
    pending = null;
  }
  return { requestReload, trackRequest, finishRequest, stop, safe, setSafeRoute: (fn) => { safeRoute = fn; } };
}

const applicationUpdate = createApplicationUpdate();
export const requestApplicationReload = (options) => applicationUpdate.requestReload(options);
export const trackApplicationRequest = (config) => applicationUpdate.trackRequest(config);
export const finishApplicationRequest = (config) => applicationUpdate.finishRequest(config);

export const SAFE_ROUTES = new Set([
  'employees', 'past', 'directory', 'candidates', 'compensation', 'reports',
  'documents', 'employee-reports', 'plan-coverage', 'departments', 'positions',
  'dashboard', 'inbox', 'all-workflows', 'attendance', 'calendar', 'tree', 'change-log',
]);

/** Build IDs are unique per build, even when package.json version is unchanged. */
export function startApplicationVersionCheck({ router, buildId = process.env.VUE_APP_BUILD_ID,
  fetchVersion = (...args) => fetch(...args), window: win = window, document: doc = document,
  timers = globalThis, updater = applicationUpdate } = {}) {
  updater.setSafeRoute(() => {
    const route = router.currentRoute.value;
    // The initial lazy route can fail before any screen has mounted.
    return SAFE_ROUTES.has(String(route?.name)) || (!route?.name && route?.matched?.length === 0);
  });
  let running = false;
  let lastChecked = 0;
  let stopped = false;
  async function check() {
    if (stopped || running || !buildId || doc.visibilityState === 'hidden' || win.navigator.onLine === false) return;
    if (Date.now() - lastChecked < 30_000) return;
    running = true;
    lastChecked = Date.now();
    const controller = new AbortController();
    const timeout = timers.setTimeout(() => controller.abort(), 8_000);
    try {
      const response = await fetchVersion(`${process.env.BASE_URL || '/'}version.json`, {
        cache: 'no-store', credentials: 'same-origin', signal: controller.signal,
      });
      if (!response.ok) return;
      const version = await response.json();
      if (!stopped && typeof version?.buildId === 'string' && version.buildId && version.buildId !== buildId) {
        updater.requestReload({ key: `hr:version-reload:${buildId}:${version.buildId}`, once: true });
      }
    } catch { /* Offline/old deployments must not interrupt the current application. */ }
    finally { timers.clearTimeout(timeout); running = false; }
  }
  const timer = timers.setInterval(check, 60_000);
  const removeRouteHook = router.afterEach(() => { void check(); });
  win.addEventListener('focus', check);
  win.addEventListener('online', check);
  doc.addEventListener('visibilitychange', check);
  void check();
  return () => {
    stopped = true;
    timers.clearInterval(timer);
    removeRouteHook();
    win.removeEventListener('focus', check);
    win.removeEventListener('online', check);
    doc.removeEventListener('visibilitychange', check);
    updater.stop();
  };
}

/** Wait for list data to settle before restoring scroll; user interaction cancels it. */
export function restoreApplicationScroll({ window: win = window, document: doc = document, timers = globalThis } = {}) {
  let saved;
  try {
    saved = JSON.parse(win.sessionStorage.getItem('hr:reload-scroll') || 'null');
    win.sessionStorage.removeItem('hr:reload-scroll');
  } catch { return; }
  if (!saved || saved.href !== win.location.href || Date.now() - saved.at > 120_000
      || !Number.isFinite(saved.x) || !Number.isFinite(saved.y)) return;
  let attempts = 0;
  const cleanup = () => {
    timers.clearInterval(timer);
    ['pointerdown', 'wheel', 'touchstart', 'keydown'].forEach((event) => win.removeEventListener(event, cleanup));
  };
  const timer = timers.setInterval(() => {
    attempts += 1;
    if (saved.href !== win.location.href || attempts > 40) { cleanup(); return; }
    if (doc.querySelector('[aria-busy="true"], .p-datatable-loading-overlay')) return;
    win.scrollTo(saved.x, saved.y);
    if (Math.abs(win.scrollY - saved.y) < 2) cleanup();
  }, 250);
  ['pointerdown', 'wheel', 'touchstart', 'keydown'].forEach((event) => win.addEventListener(event, cleanup, { passive: true }));
}
